import React from 'react';
import {
  formatDate,
  formatMarketCap,
  formatTimeAgo,
  getTrackerCount,
  getTrackerLabel,
  getTrackerRows,
} from '../utils/trackerUtils';
import TierBadge from './TierBadge';

function MilestoneTags({ items, kind = 'recovery' }) {
  if (!items?.length) return <span className="text-xs text-gray-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {[...items].sort((a, b) => a - b).slice(0, 4).map((m) => (
        <span
          key={m}
          className={`px-1.5 py-0.5 rounded text-xs ${
            kind === 'multiplier'
              ? 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
              : 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
          }`}
        >
          {kind === 'multiplier' ? `${m}x` : `+${m}%`}
        </span>
      ))}
      {items.length > 4 && (
        <span className="text-xs text-gray-400">+{items.length - 4}</span>
      )}
    </div>
  );
}

export function GroupsBadge({ item, expandedRows, toggleExpanded, rowKey }) {
  const groupCount = getTrackerCount(item);
  const trackers = getTrackerRows(item);
  const isExpanded = expandedRows.has(rowKey);
  const primary = trackers[0];

  return (
    <div>
      <button
        type="button"
        onClick={() => toggleExpanded(rowKey)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
          groupCount > 0
            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200 hover:bg-purple-200 dark:hover:bg-purple-900/60 cursor-pointer'
            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 cursor-default'
        }`}
        title="Click to expand groups"
      >
        <span className="text-[10px]">{isExpanded ? '▼' : '▶'}</span>
        {groupCount} {groupCount === 1 ? 'group' : 'groups'}
      </button>
      {!isExpanded && primary && (
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate max-w-[160px]" title={getTrackerLabel(primary)}>
          {getTrackerLabel(primary)}
        </div>
      )}
    </div>
  );
}

export default function TrackerGroupsPanel({
  item,
  tokenSymbol,
  currentMarketCap,
  recoverySnapshot,
  onTrackerClick,
  colSpan = 10,
}) {
  const trackers = getTrackerRows(item);

  return (
    <tr className="bg-gray-50/80 dark:bg-gray-900/40">
      <td colSpan={colSpan} className="px-4 py-3">
        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
          Groups tracking {tokenSymbol || item.symbol || 'token'}
        </div>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Group</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Tier</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Caller</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Added</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">IMC</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Milestones</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Recovery</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {trackers.map((tracker) => (
                <tr
                  key={`${tracker.chatId}-${tracker.addedAt}`}
                  className="hover:bg-blue-50/60 dark:hover:bg-blue-900/10 cursor-pointer"
                  onClick={() => onTrackerClick?.({
                    tracker,
                    tokenSymbol: tokenSymbol || item.symbol,
                    currentMarketCap: currentMarketCap ?? item.currentMarketCap,
                    recoverySnapshot: recoverySnapshot || item.recoverySnapshot,
                  })}
                  title="Click for full tracker details"
                >
                  <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">
                    {getTrackerLabel(tracker)}
                  </td>
                  <td className="px-4 py-2"><TierBadge tier={tracker.tier} /></td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300">
                    {tracker.addedBy || tracker.sharedBy || '—'}
                  </td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-400">
                    <div>{formatDate(tracker.addedAt)}</div>
                    <div className="text-xs text-gray-400">{formatTimeAgo(tracker.addedAt)}</div>
                  </td>
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                    {formatMarketCap(tracker.initialMarketCap)}
                  </td>
                  <td className="px-4 py-2">
                    {tracker.isRecovering ? (
                      <span className="text-green-600 dark:text-green-400 text-xs font-semibold">Recovering</span>
                    ) : (
                      <span className="text-gray-400 text-xs">Watching</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <MilestoneTags items={tracker.milestonesReached} kind="multiplier" />
                  </td>
                  <td className="px-4 py-2">
                    <MilestoneTags items={tracker.recoveryMilestonesReached} kind="recovery" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );
}
